import {
  AD_TELEMETRY_EVENTS,
  type AdImpressionProperties,
  type AdRequestSentProperties,
  type AdResponseReceivedProperties,
  type AdTelemetryEventName,
  type VastFailureStage,
} from '@/types/ad-telemetry'

export type VastTelemetryEvent =
  | {
      event: 'ad_request_sent'
      properties: AdRequestSentProperties
    }
  | {
      event: 'ad_response_received'
      properties: AdResponseReceivedProperties
    }
  | {
      event: 'ad_impression'
      properties: AdImpressionProperties
    }

type VastAdTelemetryOptions = {
  capture: (event: VastTelemetryEvent) => void
  isEventEnabled?: (event: AdTelemetryEventName) => boolean
  createRequestId?: () => string
  now?: () => number
}

type VastRequest = {
  adRequestId: string
  requestStartedAt: number
  responseSent: boolean
  impressionSent: boolean
}

type VastFailureOptions = {
  failureStage: Exclude<VastFailureStage, 'none'>
  isEmpty: boolean
  isBlocked: boolean
  error?: google.ima.AdError | null
}

const getErrorProperties = (error: google.ima.AdError | null | undefined) => ({
  ima_error_code: error?.getErrorCode() ?? null,
  ima_vast_error_code: error?.getVastErrorCode() ?? null,
  ima_error_type: error?.getType() ?? null,
  ima_error_message: error?.getMessage() ?? null,
})

export const createVastAdTelemetry = ({
  capture,
  isEventEnabled = () => true,
  createRequestId = () => crypto.randomUUID(),
  now = Date.now,
}: VastAdTelemetryOptions) => {
  let request: VastRequest | null = null

  const emit = (event: VastTelemetryEvent) => {
    if (isEventEnabled(event.event)) capture(event)
  }

  const sendResponse = ({
    isEmpty,
    isBlocked,
    failureStage,
    error,
  }: {
    isEmpty: boolean
    isBlocked: boolean
    failureStage: VastFailureStage
    error?: google.ima.AdError | null
  }) => {
    if (request === null || request.responseSent) return

    request.responseSent = true

    emit({
      event: 'ad_response_received',
      properties: {
        ad_surface: 'vast-overlay',
        ad_request_id: request.adRequestId,
        is_empty: isEmpty,
        is_blocked: isBlocked,
        response_time_ms: now() - request.requestStartedAt,
        failure_stage: failureStage,
        ...getErrorProperties(error),
      },
    })
  }

  const startRequest = ({
    hasVastTagUrl,
    isSdkReadyAtRequest,
  }: {
    hasVastTagUrl: boolean
    isSdkReadyAtRequest: boolean
  }) => {
    if (!AD_TELEMETRY_EVENTS.some((event) => isEventEnabled(event)) || request !== null) return

    request = {
      adRequestId: createRequestId(),
      requestStartedAt: now(),
      responseSent: false,
      impressionSent: false,
    }

    emit({
      event: 'ad_request_sent',
      properties: {
        ad_surface: 'vast-overlay',
        ad_request_id: request.adRequestId,
        has_vast_tag_url: hasVastTagUrl,
        is_sdk_ready_at_request: isSdkReadyAtRequest,
      },
    })
  }

  const handleLoaded = () => {
    sendResponse({
      isEmpty: false,
      isBlocked: false,
      failureStage: 'none',
    })
  }

  const handleFailure = ({ failureStage, isEmpty, isBlocked, error }: VastFailureOptions) => {
    sendResponse({ isEmpty, isBlocked, failureStage, error })
  }

  const handleAdsLoaderError = (error: google.ima.AdError | null) => {
    handleFailure({
      failureStage: 'ads_loader',
      isEmpty: error?.getVastErrorCode() === 303,
      isBlocked: false,
      error,
    })
  }

  const handleStarted = () => {
    if (request === null || !request.responseSent || request.impressionSent) return

    request.impressionSent = true

    emit({
      event: 'ad_impression',
      properties: {
        ad_surface: 'vast-overlay',
        ad_request_id: request.adRequestId,
        time_to_impression_ms: now() - request.requestStartedAt,
      },
    })
  }

  return {
    startRequest,
    handleLoaded,
    handleFailure,
    handleAdsLoaderError,
    handleStarted,
  }
}
